import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarDays, Home, TriangleAlert } from 'lucide-react';
import { SiteApplicationSteps, formDocsPayload, type FormDocs } from '../../components/site/ApplicationForm';
import { emptyApplication, toPayload, withFreeFrom, moveInTooEarly, serverFieldError, type ApplicationState } from '../../lib/applicationForm';
import { PageLoader } from '../../components/BrandLoader';
import { Money } from '../../components/ui';
import { TCard, TEmpty, TLinkButton, TPageHead } from '../../components/tenant/kit';
import { myBookingApi, viewingWhen, type MyViewing } from '../../lib/myBooking';

/**
 * Apply for the flat of a completed viewing, inside the portal (/app/my-booking/apply/:leadId — docs/plans/
 * viewing-first-onboarding.md, phase 4). The one application form in four steps (components/site/ApplicationForm.tsx
 * SiteApplicationSteps, in the applicant's own words and consents) prefilled from the viewing: first and last name, email
 * and mobile from the viewing's lead, the rent offered at the advert and the move-in from the day the flat is free. It
 * goes to POST /app/applications with the lead (lead → APPLIED, the viewing stays COMPLETED) and lands on My booking.
 * A viewing that is not completed (or a flat no longer free) shows why instead.
 */
export default function ApplyFromViewingPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const viewings = useQuery({ queryKey: ['my-viewings'], queryFn: myBookingApi.viewings });
  if (viewings.isLoading) return <PageLoader />;
  const mine = (viewings.data ?? []).filter((v) => v.leadId === leadId);
  const v = mine.find((x) => x.canApply) ?? mine[0] ?? null;
  const back = <TLinkButton to="/app/my-booking" variant="soft" icon={ArrowLeft}>Back to My booking</TLinkButton>;
  if (!v) return <TEmpty icon={TriangleAlert} title="This viewing could not be found" body="It may have been booked with another email address." action={back} />;
  if (!v.canApply) {
    return (
      <TEmpty icon={CalendarDays} title={v.applicationId ? 'You have applied for this flat' : 'Not open for applying yet'}
        body={v.applyBlockedBy ?? 'You can apply once your viewing has taken place — Lotus Living marks it completed, then “Apply for this flat” opens.'} action={back} />
    );
  }
  return <ApplyForm v={v} />;
}

function ApplyForm({ v }: { v: MyViewing }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [s, setS] = useState<ApplicationState>(() => withFreeFrom(emptyApplication({
    firstName: v.applicant.firstName, lastName: v.applicant.lastName, email: v.applicant.email ?? '', mobile: v.applicant.mobile ?? '',
    offeredRent: v.rentMinor ? String(v.rentMinor / 100) : '',
  }), v.availableFrom, { reset: true }));
  const [docs, setDocs] = useState<FormDocs>({});
  const set = (patch: Partial<ApplicationState>) => setS((x) => ({ ...x, ...patch }));

  const submit = useMutation({
    mutationFn: () => myBookingApi.applyFromViewing({ leadId: v.leadId, listingId: v.listing.id, form: toPayload(s), documents: formDocsPayload(s.nationality, docs) }),
    onSuccess: (r) => {
      qc.setQueryData(['my-booking'], r.booking);
      void qc.invalidateQueries({ queryKey: ['my-viewings'] });
      navigate('/app/my-booking');
    },
    // The flat's free date moved while the page was open: the move-in field says so under it.
    onError: (e) => { const early = moveInTooEarly(e); if (early) setS((x) => ({ ...x, freeFrom: early.availableFrom })); },
  });
  const notice = submit.isError && !serverFieldError(submit.error) ? <p className="site-error">{(submit.error as Error).message}</p> : null;

  return (
    <div className="space-y-4 sm:space-y-5">
      <TPageHead overline="Apply for this flat" title={`Flat ${v.unit.reference}, ${v.property.name}`}
        sub="Your details from the viewing are filled in. Four short steps — no fees for applying or referencing."
        action={<TLinkButton to="/app/my-booking" variant="soft" small icon={ArrowLeft}>My booking</TLinkButton>} />
      <TCard i={0}>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-ink-100 pb-4 text-[13.5px] text-ink-600">
          <span className="inline-flex items-center gap-1.5"><Home className="h-4 w-4 text-bronze-600" /><Money minor={v.rentMinor} currency={v.currency} /> a month</span>
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-bronze-600" />Viewed {viewingWhen(v.scheduledAt)}</span>
        </div>
        <SiteApplicationSteps
          s={s} set={set} advert={v.rentMinor} docs={docs} setDocs={setDocs}
          submitLabel="Submit application" pending={submit.isPending} onSubmit={() => submit.mutate()} error={submit.error}
          notice={notice} footnote="No fees for applying or referencing."
        />
      </TCard>
    </div>
  );
}
